import ContentUpload from 'module/ContentUpload/ContentUpload';

import React, { useEffect, useMemo } from 'react';

import { DownloadOutlined, FileOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, Row, Switch, Tooltip } from 'antd';
import { useGetArticleContentQuery, useMakeAssigmentDecisionMutation } from 'services';
import { downloadBlob, openNotification } from 'utils';
import { fileDownload } from 'fetcher';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { fetchError } from 'slices/commonSlice';
import { useApiErrorMessage } from 'hooks';

const handleBlobDownload = async (fileUrl, fileName) => {
  const { data } = await fileDownload(fileUrl);
  downloadBlob(data, fileName);
};
const contentFile = (fileName, fileUrl) => {
  return (
    <Card>
      <div className="gx-d-flex gx-align-items-center gx-justify-content-between">
        <div className="gx-d-flex gx-fit-text gx-align-items-center">
          <FileOutlined />
          <div className="gx-ml-2">{fileName}</div>
        </div>
        <Button
          type="link"
          icon={<DownloadOutlined className="gx-fs-xl" />}
          onClick={() => handleBlobDownload(fileUrl, fileName)}
        />
      </div>
    </Card>
  );
};

/**
 * @description Madde dosyalarını (madde dosyası / kimlik kartı / ÜVKA) gösterir ve yetki varsa aşamayı
 * onaylayan karar formunu (`PUT /assignments/{id}/decide`) basar. Medya teklifi tablosu `children`
 * olarak gömülür.
 *
 * @param {number|string} props.offerID - `ArticleOffer` id'si; dosyalar ve karar bu id ile gider.
 * @param {string} [props.description] - Dış kaynaklı açıklama; form alanlarının yeniden set edilmesini
 *   tetikleyen bağımlılık olarak kullanılır.
 * @param {boolean} [props.isProcessContinue=false] - Aşama halen bu adımdaysa (yani karar verilebilir
 *   durumdaysa) `true`. Karar formu ve "Gönder" yalnız bu durumda render edilir.
 * @param {boolean} [props.isMediaEditable=true] - Current_user bu adımın karar sahibi mi. `false` ise
 *   form hiç basılmaz (salt-görüntüleme).
 * @param {string|null} [props.submitBlockReason=null] - Aşama onayı bir iş kuralı yüzünden engelliyse
 *   engelin gerekçesi (ör. karar verilmemiş medya teklifi kaldıysa —
 *   bkz. `useMediaOfferDecisionGuard`). Doluysa "Gönder" kapatılır ve metin tooltip olarak gösterilir;
 *   `null` ise buton normal çalışır. Gerekçeyi hesaplayan taraf çağırandır — bu bileşen yalnız gösterir.
 * @param {React.ReactNode} [props.children=null] - Dosyaların altına gömülecek içerik (medya teklifi tablosu).
 */
const MediaField = ({ offerID, description, isProcessContinue = false, isMediaEditable = true, submitBlockReason = null, children = null }) => {
  const [form] = Form.useForm();
  const switchValue = Form.useWatch('switch', form);
  const { data } = useGetArticleContentQuery({
    offerID,
  });
  const itemFile = useMemo(() => {
    return data?.contents.find((item) => item.type === 0);
  }, [data]);
  const idCard = useMemo(() => {
    return data?.contents.find((item) => item.type === 1);
  }, [data]);
  const uvkaFile = useMemo(() => {
    return data?.contents.find((item) => item.type === 3);
  }, [data]);

  const [makeAssigmentDecision] = useMakeAssigmentDecisionMutation();

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const getErrorMessage = useApiErrorMessage();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        itemFile: data.contents.find((item) => item.type === 0),
        idCard: data.contents.find((item) => item.type === 1),
        uvkaFile: data.contents.find((item) => item.type === 3),
      });
    }
  }, [data, description, form]);

  const handleSubmit = async (values) => {
    const body = {
      opinion: 'approved',
      ...(values.description && { description: values.description }),
    };

    const { error } = await makeAssigmentDecision({
      offerID,
      data: body,
    });

    // BE bu aşamada iş kuralı ihlallerini `hydra:description` ile bildiriyor (ör. medya
    // tekliflerine karar verilmeden onaylama denemesi) → mesajı olduğu gibi göster, generic
    // "Hata Kodu: 400" metnine düşme.
    if (error) {
      return dispatch(fetchError(getErrorMessage(error)));
    }
    openNotification({
      message: formatMessage({
        id: 'appModule.actionSuccessfully',
      }),
    });
    form.resetFields();
  };

  return (
    <>
      <div className="gx-d-flex gx-align-items-center gx-justify-content-between">
        <h3>Madde Dosyası</h3>
      </div>
      <Card className="gx-mt-3">
        {itemFile && (
          <Col>
            <h4>Madde Dosyası</h4> {contentFile(itemFile.media[0].name, itemFile.media[0].fileFullUrl)}
          </Col>
        )}

        {idCard && (
          <>
            <h4>Kimlik Kartı</h4>
            {contentFile(idCard.media[0].name, idCard.media[0].fileFullUrl)}
          </>
        )}
        {uvkaFile && (
          <>
            <h4>ÜVKA Dosyası</h4>
            {contentFile(uvkaFile.media[0].name, uvkaFile.media[0].fileFullUrl)}
          </>
        )}
        {children}

        {isMediaEditable && isProcessContinue && (
          <Form
            id="mediaForm"
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              className="gx-mt-5 gx-mb-5"
              label="Açıklama"
              name="description"
            >
              <Input.TextArea
                placeholder="İletmek istediğiniz not varsa yazabilirsiniz."
                rows={5}
                showCount
              />
            </Form.Item>
            <Card>
              <div className="gx-d-flex gx-justify-content-between">
                Madde dosyalarında değişiklik yapmak istiyor musunuz?{' '}
                <Form.Item
                  name="switch"
                  valuePropName="checked"
                  normalize={(value) => (!value ? undefined : value)}
                >
                  <Switch />
                </Form.Item>
              </div>
            </Card>
            {switchValue && (
              <div className="gx-mt-5">
                <ContentUpload
                  id={offerID}
                  isIdCardRequired={true}
                />
              </div>
            )}
            {isProcessContinue && (
              <Row
                className="gx-mt-5"
                gutter={24}
                style={{
                  flexDirection: 'row',
                }}
              >
                <Col sm={24}>
                  {/* Tooltip'in disabled butonda çalışması için araya <span> gerekir (disabled buton
                      pointer event almaz) — aynı kalıp ArticleStatus/index.js'te de kullanılıyor. */}
                  <Tooltip title={submitBlockReason}>
                    <span>
                      <Button
                        form="mediaForm"
                        block
                        htmlType="submit"
                        disabled={Boolean(submitBlockReason)}
                      >
                        Gönder
                      </Button>
                    </span>
                  </Tooltip>
                </Col>
              </Row>
            )}
          </Form>
        )}
      </Card>
    </>
  );
};

export default MediaField;
